/**
 * Decal Generator
 * Creates SVG decal designs with embedded QR codes for print-at-home referral materials
 * Ported from mobile: packages/mobile/utils/decal-generator.ts
 */

import { BRAND_COLORS, DECAL_SIZES } from './constants';
import type { DecalType, DecalSize, DecalResult } from '../types';

/**
 * Generate a circular sticker decal SVG
 */
function generateCircularDecal(
  url: string,
  referralCode: string,
  size: { width: number; height: number }
): string {
  const { width, height } = size;
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 2 - 10;
  const qrSize = radius * 0.9;
  const qrX = centerX - qrSize / 2;
  const qrY = centerY - qrSize / 2 + radius * 0.05;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">
  <defs>
    <clipPath id="circleClip">
      <circle cx="${centerX}" cy="${centerY}" r="${radius}"/>
    </clipPath>
    <style>
      .title { font-family: Arial, sans-serif; font-weight: bold; fill: ${BRAND_COLORS.white}; }
      .subtitle { font-family: Arial, sans-serif; fill: ${BRAND_COLORS.white}; }
      .code { font-family: 'Courier New', monospace; font-weight: bold; fill: ${BRAND_COLORS.secondary}; }
    </style>
  </defs>

  <!-- Background circle -->
  <circle cx="${centerX}" cy="${centerY}" r="${radius}" fill="${BRAND_COLORS.primary}"/>

  <!-- Inner white circle for QR code -->
  <circle cx="${centerX}" cy="${centerY + radius * 0.05}" r="${qrSize / 2 + 15}" fill="${BRAND_COLORS.white}"/>

  <!-- RUMIO text at top -->
  <text x="${centerX}" y="${centerY - radius * 0.65}" text-anchor="middle" class="title" font-size="${radius * 0.18}">RUMIO</text>

  <!-- QR Code placeholder -->
  <rect x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}" fill="${BRAND_COLORS.white}" stroke="${BRAND_COLORS.lightGray}" stroke-width="2"/>
  <text x="${centerX}" y="${centerY + radius * 0.05}" text-anchor="middle" font-family="Arial" font-size="${radius * 0.08}" fill="${BRAND_COLORS.primary}">
    [QR CODE]
  </text>
  <text x="${centerX}" y="${centerY + radius * 0.15}" text-anchor="middle" font-family="Arial" font-size="${radius * 0.05}" fill="#666">
    ${url}
  </text>

  <!-- Referral code -->
  <text x="${centerX}" y="${centerY + radius * 0.55}" text-anchor="middle" class="code" font-size="${radius * 0.12}">${referralCode}</text>

  <!-- Call to action at bottom -->
  <text x="${centerX}" y="${centerY + radius * 0.75}" text-anchor="middle" class="subtitle" font-size="${radius * 0.08}">Scan for IKEA deals!</text>

  <!-- Cut line (dashed circle outside) -->
  <circle cx="${centerX}" cy="${centerY}" r="${radius + 5}" fill="none" stroke="#ccc" stroke-width="1" stroke-dasharray="5,5"/>
</svg>`;
}

/**
 * Generate a rectangular bumper sticker style decal SVG
 */
function generateRectangularDecal(
  _url: string,
  referralCode: string,
  size: { width: number; height: number }
): string {
  const { width } = size;
  const aspectRatio = 3; // 3:1 aspect ratio for bumper sticker
  const actualHeight = width / aspectRatio;
  const padding = width * 0.03;
  const qrSize = actualHeight * 0.7;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${actualHeight}" width="${width}" height="${actualHeight}">
  <defs>
    <style>
      .brand { font-family: Arial, sans-serif; font-weight: bold; fill: ${BRAND_COLORS.white}; }
      .tagline { font-family: Arial, sans-serif; fill: ${BRAND_COLORS.white}; }
      .code { font-family: 'Courier New', monospace; font-weight: bold; fill: ${BRAND_COLORS.secondary}; }
    </style>
    <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:${BRAND_COLORS.primary};stop-opacity:1" />
      <stop offset="100%" style="stop-color:#004080;stop-opacity:1" />
    </linearGradient>
  </defs>

  <!-- Background with rounded corners -->
  <rect x="0" y="0" width="${width}" height="${actualHeight}" rx="${actualHeight * 0.1}" fill="url(#bgGradient)"/>

  <!-- RUMIO brand on left -->
  <text x="${padding * 2}" y="${actualHeight * 0.45}" class="brand" font-size="${actualHeight * 0.25}">RUMIO</text>
  <text x="${padding * 2}" y="${actualHeight * 0.7}" class="tagline" font-size="${actualHeight * 0.1}">Furnish for less!</text>

  <!-- QR Code in center -->
  <rect x="${width * 0.35}" y="${(actualHeight - qrSize) / 2}" width="${qrSize}" height="${qrSize}" fill="${BRAND_COLORS.white}" rx="5"/>
  <text x="${width * 0.35 + qrSize / 2}" y="${actualHeight / 2}" text-anchor="middle" font-family="Arial" font-size="${qrSize * 0.15}" fill="${BRAND_COLORS.primary}">
    [QR]
  </text>

  <!-- Code below QR -->
  <text x="${width * 0.35 + qrSize / 2}" y="${actualHeight * 0.88}" text-anchor="middle" class="code" font-size="${actualHeight * 0.12}">${referralCode}</text>

  <!-- Call to action on right -->
  <text x="${width * 0.72}" y="${actualHeight * 0.35}" class="brand" font-size="${actualHeight * 0.12}">Scan for</text>
  <text x="${width * 0.72}" y="${actualHeight * 0.52}" class="brand" font-size="${actualHeight * 0.12}">As-Is deals</text>
  <text x="${width * 0.72}" y="${actualHeight * 0.69}" class="brand" font-size="${actualHeight * 0.12}">at IKEA!</text>

  <!-- Cut line -->
  <rect x="-2" y="-2" width="${width + 4}" height="${actualHeight + 4}" rx="${actualHeight * 0.1 + 2}" fill="none" stroke="#ccc" stroke-width="1" stroke-dasharray="5,5"/>
</svg>`;
}

/**
 * Generate a square badge style decal SVG
 */
function generateSquareDecal(
  url: string,
  referralCode: string,
  size: { width: number; height: number }
): string {
  const { width } = size;
  const qrSize = width * 0.5;
  const qrX = (width - qrSize) / 2;
  const qrY = width * 0.28;

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${width}" width="${width}" height="${width}">
  <defs>
    <style>
      .brand { font-family: Arial, sans-serif; font-weight: bold; fill: ${BRAND_COLORS.white}; }
      .tagline { font-family: Arial, sans-serif; fill: ${BRAND_COLORS.white}; }
      .code { font-family: 'Courier New', monospace; font-weight: bold; fill: ${BRAND_COLORS.secondary}; }
      .url { font-family: Arial, sans-serif; fill: rgba(255,255,255,0.7); }
    </style>
  </defs>

  <!-- Background with rounded corners -->
  <rect x="0" y="0" width="${width}" height="${width}" rx="${width * 0.05}" fill="${BRAND_COLORS.primary}"/>

  <!-- Yellow accent bar at top -->
  <rect x="0" y="0" width="${width}" height="${width * 0.08}" rx="${width * 0.05}" fill="${BRAND_COLORS.secondary}"/>
  <rect x="0" y="${width * 0.04}" width="${width}" height="${width * 0.04}" fill="${BRAND_COLORS.secondary}"/>

  <!-- RUMIO brand -->
  <text x="${width / 2}" y="${width * 0.2}" text-anchor="middle" class="brand" font-size="${width * 0.12}">RUMIO</text>

  <!-- QR Code area -->
  <rect x="${qrX - 8}" y="${qrY - 8}" width="${qrSize + 16}" height="${qrSize + 16}" fill="${BRAND_COLORS.white}" rx="8"/>
  <text x="${width / 2}" y="${qrY + qrSize / 2}" text-anchor="middle" font-family="Arial" font-size="${qrSize * 0.12}" fill="${BRAND_COLORS.primary}">
    [QR CODE]
  </text>
  <text x="${width / 2}" y="${qrY + qrSize / 2 + qrSize * 0.15}" text-anchor="middle" font-family="Arial" font-size="${qrSize * 0.06}" fill="#888">
    ${url}
  </text>

  <!-- Referral code -->
  <text x="${width / 2}" y="${width * 0.85}" text-anchor="middle" class="code" font-size="${width * 0.08}">${referralCode}</text>

  <!-- Call to action -->
  <text x="${width / 2}" y="${width * 0.94}" text-anchor="middle" class="tagline" font-size="${width * 0.045}">Scan for IKEA As-Is deals!</text>

  <!-- Cut line -->
  <rect x="-2" y="-2" width="${width + 4}" height="${width + 4}" rx="${width * 0.05 + 2}" fill="none" stroke="#ccc" stroke-width="1" stroke-dasharray="5,5"/>
</svg>`;
}

/**
 * Generate decal SVG based on type and size
 */
export function generateDecalSvg(
  url: string,
  referralCode: string,
  type: DecalType,
  sizeKey: DecalSize
): string {
  const size = DECAL_SIZES[sizeKey] || DECAL_SIZES.medium;

  switch (type) {
    case 'circular':
      return generateCircularDecal(url, referralCode, size);
    case 'rectangular':
      return generateRectangularDecal(url, referralCode, size);
    case 'square':
    default:
      return generateSquareDecal(url, referralCode, size);
  }
}

/**
 * Generate decal result with metadata
 */
export function generateDecal(
  url: string,
  referralCode: string,
  type: DecalType,
  size: DecalSize
): DecalResult {
  const svg = generateDecalSvg(url, referralCode, type, size);
  const sizeLabel = DECAL_SIZES[size]?.label || size;
  const filename = `rumio-${type}-${sizeLabel.replace('"', 'in')}-${referralCode}.${size === 'vector' ? 'svg' : 'png'}`;

  return {
    svg,
    type,
    size,
    filename,
  };
}

/**
 * Generate all decal variants for a referral code
 */
export function generateAllDecals(
  url: string,
  referralCode: string
): Record<DecalType, Record<DecalSize, DecalResult>> {
  const types: DecalType[] = ['circular', 'rectangular', 'square'];
  const sizes: DecalSize[] = ['small', 'medium', 'large', 'vector'];

  const results = {} as Record<DecalType, Record<DecalSize, DecalResult>>;

  for (const type of types) {
    results[type] = {} as Record<DecalSize, DecalResult>;
    for (const size of sizes) {
      results[type][size] = generateDecal(url, referralCode, type, size);
    }
  }

  return results;
}

/**
 * Get dimensions for a decal type and size
 */
export function getDecalDimensions(
  type: DecalType,
  sizeKey: DecalSize
): { width: number; height: number } {
  const size = DECAL_SIZES[sizeKey] || DECAL_SIZES.medium;

  if (type === 'rectangular') {
    // 3:1 aspect ratio for bumper sticker
    return { width: size.width, height: Math.round(size.width / 3) };
  }

  return { width: size.width, height: size.height };
}
